/*
 * Artemax favorites, v2 (2026-09-16).
 *
 * Visitors keep the artworks they love in this browser. No account, no
 * network for the list itself: it lives in localStorage under one key and is
 * never sent anywhere. Four surfaces share the one store:
 *
 *   - the heart in the artwork action row (artemax-product-actions.js)
 *   - the heart beside the cart, which opens the favorites drawer
 *   - the favorites drawer (<artemax-favorites-drawer>), where every kept
 *     piece can be reviewed, its variation chosen, and added to the cart
 *   - the favorites page (/pages/favorites), the same list at full width
 *
 * A favorite is one artwork in one colorway: the key is the product handle
 * plus the variant on screen when the heart was tapped. In the drawer and on
 * the page each card loads the product's public JSON (/products/<handle>.js)
 * on demand, offers the product's options preselected to the kept variant,
 * and adds the chosen variant through the theme's own cart flow: POST
 * /cart/add with the cart section ids, then a `cart:update` event exactly as
 * Horizon's product form dispatches it, so the cart bubble and cart drawer
 * refresh natively. Cross-tab changes propagate via the storage event. If
 * storage is unavailable the list lives in memory for the page.
 */
(function () {
  'use strict';

  if (window.ArtemaxFavorites) return;

  var KEY = 'artemax:favorites:v1';
  var EVENT = 'artemax:favorites:change';
  var MAX = 200;
  var memory = null;
  var productCache = new Map();

  function valid(item) {
    return !!item && typeof item.h === 'string' && item.h !== '' && typeof item.u === 'string' && item.u !== '';
  }

  function read() {
    if (memory) return memory.slice();
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(valid) : [];
    } catch (e) {
      return [];
    }
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { count: read().length } }));
  }

  function write(list) {
    var trimmed = list.slice(0, MAX);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(trimmed));
      memory = null;
    } catch (e) {
      memory = trimmed;
    }
    broadcast();
  }

  function keyOf(handle, variant) {
    return String(handle || '') + '#' + String(variant || '');
  }

  function normalize(item) {
    return {
      h: String(item.h || ''),
      v: item.v ? String(item.v) : '',
      u: String(item.u || ''),
      t: String(item.t || ''),
      c: String(item.c || ''),
      i: String(item.i || ''),
      at: Date.now()
    };
  }

  function has(handle, variant) {
    var k = keyOf(handle, variant);
    return read().some(function (item) { return keyOf(item.h, item.v) === k; });
  }

  function remove(handle, variant) {
    var k = keyOf(handle, variant);
    write(read().filter(function (item) { return keyOf(item.h, item.v) !== k; }));
  }

  function add(item) {
    var entry = normalize(item);
    if (!valid(entry)) return false;
    var rest = read().filter(function (existing) { return keyOf(existing.h, existing.v) !== keyOf(entry.h, entry.v); });
    write([entry].concat(rest));
    return true;
  }

  function toggle(item) {
    if (has(item.h, item.v)) { remove(item.h, item.v); return false; }
    return add(item);
  }

  // Shopify CDN image URLs carry ?width=; re-size for the surface rendering it.
  function sizedUrl(src, width) {
    var clean = String(src || '').split('#')[0]
      .replace(/([?&])width=\d+&?/g, '$1')
      .replace(/[?&]$/, '');
    if (!clean) return '';
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function srcsetFor(src) {
    var widths = [240, 352, 480, 640, 832, 1200];
    return widths.map(function (w) { return sizedUrl(src, w) + ' ' + w + 'w'; }).join(', ');
  }

  function bindCounts() {
    var nodes = document.querySelectorAll('[data-artemax-favorites-count]');
    var count = read().length;
    nodes.forEach(function (node) {
      node.textContent = count > 99 ? '99+' : String(count);
      node.hidden = count === 0;
      var host = node.closest('[data-artemax-favorites-link]');
      if (host) host.setAttribute('data-has-favorites', count > 0 ? 'true' : 'false');
    });
    document.querySelectorAll('[data-artemax-favorites-total]').forEach(function (node) {
      node.textContent = String(count);
    });
  }

  function element(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  function money(cents, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  // Public product JSON: options, variants, availability, prices. Cached per
  // handle for the page lifetime; a failed load leaves the card in its
  // "open the artwork" state, never a broken control.
  function loadProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('product unavailable');
      return response.json();
    }).catch(function () {
      productCache.delete(handle);
      return null;
    });
    productCache.set(handle, promise);
    return promise;
  }

  function optionValues(product, index) {
    var seen = [];
    (product.variants || []).forEach(function (variant) {
      var value = variant['option' + (index + 1)];
      if (value !== null && value !== undefined && seen.indexOf(value) === -1) seen.push(value);
    });
    return seen;
  }

  function matchVariant(product, selections) {
    return (product.variants || []).find(function (variant) {
      return selections.every(function (value, index) { return variant['option' + (index + 1)] === value; });
    }) || null;
  }

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (node) {
      if (node.dataset && node.dataset.sectionId) ids.push(node.dataset.sectionId);
    });
    return ids;
  }

  function routes() {
    var theme = window.Theme && window.Theme.routes ? window.Theme.routes : {};
    return {
      add: theme.cart_add_url || '/cart/add',
      cart: theme.cart_url || '/cart'
    };
  }

  // The theme's own add-to-cart contract (assets/product-form.js): POST the
  // items with the cart section ids, read the cart back, then dispatch the
  // `cart:update` event the cart icon and cart drawer already listen for.
  function addToCart(variantId) {
    var sections = cartSectionIds();
    return fetch(routes().add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections.join(',')
      })
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok || payload.status) {
          var error = new Error(payload.message || payload.description || 'Could not add to cart');
          error.recoverable = true;
          throw error;
        }
        return payload;
      });
    }).then(function (payload) {
      return fetch('/cart.js', { credentials: 'same-origin', headers: { accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; })
        .then(function (cart) {
          var event = new CustomEvent('cart:update', { bubbles: true });
          event.detail = {
            resource: cart || undefined,
            sourceId: 'artemax-favorites',
            data: {
              source: 'artemax-favorites',
              itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined,
              variantId: String(variantId),
              sections: payload.sections
            }
          };
          document.dispatchEvent(event);
          return cart;
        });
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); return true; } catch (e) { /* fall through */ }
    }
    return false;
  }

  // ---------------------------------------------------------------- list
  var ListElement = (function () {
    function ArtemaxFavoritesList() {
      return Reflect.construct(HTMLElement, [], ArtemaxFavoritesList);
    }
    ArtemaxFavoritesList.prototype = Object.create(HTMLElement.prototype);
    ArtemaxFavoritesList.prototype.constructor = ArtemaxFavoritesList;

    ArtemaxFavoritesList.prototype.connectedCallback = function () {
      var self = this;
      this.render();
      this.onChange = function () { self.render(); };
      document.addEventListener(EVENT, this.onChange);
      this.addEventListener('click', function (event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        var removeButton = target.closest('[data-artemax-favorite-remove]');
        if (removeButton && self.contains(removeButton)) {
          event.preventDefault();
          remove(removeButton.getAttribute('data-handle'), removeButton.getAttribute('data-variant'));
          return;
        }
        var addButton = target.closest('[data-artemax-favorite-add]');
        if (addButton && self.contains(addButton)) {
          event.preventDefault();
          self.addFromCard(addButton.closest('.artemax-favorites__item'));
        }
      });
      this.addEventListener('change', function (event) {
        var select = event.target instanceof HTMLSelectElement ? event.target : null;
        if (!select || !select.hasAttribute('data-artemax-option')) return;
        self.syncCard(select.closest('.artemax-favorites__item'));
      });
    };

    ArtemaxFavoritesList.prototype.disconnectedCallback = function () {
      document.removeEventListener(EVENT, this.onChange);
    };

    ArtemaxFavoritesList.prototype.label = function (name, fallback) {
      return this.getAttribute('data-' + name) || fallback;
    };

    ArtemaxFavoritesList.prototype.render = function () {
      var items = read();
      var self = this;
      while (this.firstChild) this.removeChild(this.firstChild);
      this.setAttribute('data-count', String(items.length));

      if (!items.length) {
        var empty = element('div', 'artemax-favorites__empty');
        empty.appendChild(element('h2', 'artemax-favorites__empty-title', this.label('empty-title', 'No favorites yet')));
        empty.appendChild(element('p', 'artemax-favorites__empty-text', this.label('empty-text', 'Tap the heart on any artwork to keep it here.')));
        var browse = element('a', 'artemax-favorites__browse', this.label('browse-label', 'Browse the collection'));
        browse.href = this.getAttribute('data-browse-url') || '/';
        empty.appendChild(browse);
        this.appendChild(empty);
        return;
      }

      // Founder 2026-09-17: no words above the pieces; the menus speak for
      // themselves (option names stay on each menu's aria-label).
      var grid = element('ul', 'artemax-favorites__grid');
      grid.setAttribute('role', 'list');
      items.forEach(function (item, index) {
        grid.appendChild(self.card(item, index));
      });
      this.appendChild(grid);
    };

    ArtemaxFavoritesList.prototype.card = function (item, index) {
      var self = this;
      var li = element('li', 'artemax-favorites__item');
      li.setAttribute('data-handle', item.h);
      li.setAttribute('data-variant', item.v || '');

      var link = element('a', 'artemax-favorites__media');
      link.href = item.u;
      link.setAttribute('aria-label', item.t || item.h);
      if (item.i) {
        var img = new Image();
        img.className = 'artemax-favorites__image';
        img.alt = item.t || '';
        img.decoding = 'async';
        img.loading = index < 4 ? 'eager' : 'lazy';
        img.sizes = '(min-width: 750px) 320px, calc((100vw - 40px) / 2)';
        img.srcset = srcsetFor(item.i);
        img.src = sizedUrl(item.i, 640);
        link.appendChild(img);
      } else {
        link.appendChild(element('span', 'artemax-favorites__no-image', item.t || item.h));
      }
      li.appendChild(link);

      var meta = element('div', 'artemax-favorites__meta');
      var title = element('a', 'artemax-favorites__title', item.t || item.h);
      title.href = item.u;
      meta.appendChild(title);
      if (item.c) meta.appendChild(element('span', 'artemax-favorites__colorway', item.c));
      var price = element('span', 'artemax-favorites__price', '');
      price.setAttribute('data-artemax-price', '');
      meta.appendChild(price);
      li.appendChild(meta);

      var controls = element('div', 'artemax-favorites__controls');
      var options = element('div', 'artemax-favorites__options');
      options.setAttribute('data-artemax-options', '');
      controls.appendChild(options);

      var actions = element('div', 'artemax-favorites__actions');
      var addButton = element('button', 'artemax-favorites__add', this.label('add-label', 'Add to cart'));
      addButton.type = 'button';
      addButton.setAttribute('data-artemax-favorite-add', '');
      addButton.disabled = true;
      actions.appendChild(addButton);
      var removeButton = element('button', 'artemax-favorites__remove', this.label('remove-label', 'Remove'));
      removeButton.type = 'button';
      removeButton.setAttribute('data-artemax-favorite-remove', '');
      removeButton.setAttribute('data-handle', item.h);
      removeButton.setAttribute('data-variant', item.v || '');
      removeButton.setAttribute('aria-label', this.label('remove-label', 'Remove') + ': ' + (item.t || item.h));
      actions.appendChild(removeButton);
      controls.appendChild(actions);

      var status = element('p', 'artemax-favorites__status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('data-artemax-status', '');
      controls.appendChild(status);
      li.appendChild(controls);

      loadProduct(item.h).then(function (product) {
        if (!li.isConnected) return;
        if (!product || !Array.isArray(product.variants) || !product.variants.length) {
          addButton.hidden = true;
          var open = element('a', 'artemax-favorites__open', self.label('open-label', 'Open the artwork'));
          open.href = item.u;
          actions.insertBefore(open, removeButton);
          return;
        }
        li.product = product;
        var kept = product.variants.find(function (variant) { return String(variant.id) === String(item.v); })
          || product.variants.find(function (variant) { return variant.available; })
          || product.variants[0];
        (product.options || []).forEach(function (name, optionIndex) {
          var values = optionValues(product, optionIndex);
          if (values.length < 2 && (product.options || []).length === 1 && values[0] === 'Default Title') return;
          var field = element('label', 'artemax-favorites__option');
          var select = element('select', 'artemax-favorites__select');
          select.setAttribute('data-artemax-option', String(optionIndex));
          select.setAttribute('aria-label', String(name));
          values.forEach(function (value) {
            var option = element('option', '', String(value));
            option.value = String(value);
            if (kept && kept['option' + (optionIndex + 1)] === value) option.selected = true;
            select.appendChild(option);
          });
          field.appendChild(select);
          field.appendChild(element('span', 'artemax-favorites__measure'));
          options.appendChild(field);
        });
        self.syncCard(li);
      });

      return li;
    };

    ArtemaxFavoritesList.prototype.selectedVariant = function (li) {
      var product = li && li.product;
      if (!product) return null;
      var selects = li.querySelectorAll('select[data-artemax-option]');
      if (!selects.length) return product.variants[0] || null;
      var selections = [];
      selects.forEach(function (select) { selections[Number(select.getAttribute('data-artemax-option'))] = select.value; });
      return matchVariant(product, selections);
    };

    // Founder 2026-09-17: each menu is exactly as wide as the word it shows,
    // so the chevron sits right beside "A", "White" or "Green" instead of at
    // the far edge of the widest option. Measured with a hidden span in the
    // same font; skipped while the drawer is closed (nothing has a width).
    function fitSelect(select) {
      var probe = select.parentNode && select.parentNode.querySelector('.artemax-favorites__measure');
      var option = select.options[select.selectedIndex];
      if (!probe || !option) return;
      probe.textContent = option.text;
      var width = probe.offsetWidth;
      if (width > 0) select.style.width = (width + 20) + 'px';
    }

    ArtemaxFavoritesList.prototype.fitSelects = function () {
      this.querySelectorAll('select[data-artemax-option]').forEach(fitSelect);
    };

    ArtemaxFavoritesList.prototype.syncCard = function (li) {
      if (!li) return;
      li.querySelectorAll('select[data-artemax-option]').forEach(fitSelect);
      var variant = this.selectedVariant(li);
      var addButton = li.querySelector('[data-artemax-favorite-add]');
      var price = li.querySelector('[data-artemax-price]');
      var currency = this.getAttribute('data-currency') || 'USD';
      var locale = this.getAttribute('data-locale') || undefined;
      if (!variant) {
        if (addButton) { addButton.disabled = true; addButton.textContent = this.label('unavailable-label', 'Unavailable'); }
        if (price) price.textContent = '';
        return;
      }
      li.setAttribute('data-selected-variant', String(variant.id));
      if (price) price.textContent = money(variant.price, currency, locale);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available
          ? this.label('add-label', 'Add to cart')
          : this.label('sold-out-label', 'Sold out');
      }
      // Keep the kept image in step with the chosen variation when it has one.
      var img = li.querySelector('.artemax-favorites__image');
      var media = variant.featured_image && variant.featured_image.src;
      if (img && media) {
        img.srcset = srcsetFor(media);
        img.src = sizedUrl(media, 640);
      }
    };

    ArtemaxFavoritesList.prototype.addFromCard = function (li) {
      var self = this;
      if (!li) return;
      var variant = this.selectedVariant(li);
      var addButton = li.querySelector('[data-artemax-favorite-add]');
      var status = li.querySelector('[data-artemax-status]');
      if (!variant || !variant.available || !addButton || addButton.disabled) return;
      addButton.disabled = true;
      addButton.textContent = this.label('adding-label', 'Adding…');
      if (status) status.textContent = '';
      addToCart(variant.id).then(function () {
        addButton.textContent = self.label('added-label', 'Added');
        if (status) status.textContent = self.label('added-status', 'Added to your cart.');
        setTimeout(function () {
          addButton.disabled = false;
          addButton.textContent = self.label('add-label', 'Add to cart');
        }, 1600);
        var drawer = self.closest('artemax-favorites-drawer');
        if (drawer && typeof drawer.close === 'function') drawer.close();
        if (!openCartDrawer() && self.getAttribute('data-after-add') === 'cart') {
          window.location.assign(routes().cart);
        }
      }).catch(function (error) {
        addButton.disabled = false;
        addButton.textContent = self.label('add-label', 'Add to cart');
        if (status) {
          status.textContent = error && error.recoverable && error.message
            ? error.message
            : self.label('add-failed', 'Could not add to cart. Please open the artwork and try again.');
        }
      });
    };

    return ArtemaxFavoritesList;
  })();

  // ---------------------------------------------------------------- drawer
  var DrawerElement = (function () {
    function ArtemaxFavoritesDrawer() {
      return Reflect.construct(HTMLElement, [], ArtemaxFavoritesDrawer);
    }
    ArtemaxFavoritesDrawer.prototype = Object.create(HTMLElement.prototype);
    ArtemaxFavoritesDrawer.prototype.constructor = ArtemaxFavoritesDrawer;

    ArtemaxFavoritesDrawer.prototype.connectedCallback = function () {
      var self = this;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.addEventListener('click', function (event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-artemax-favorites-open]')) { event.preventDefault(); self.open(); return; }
        if (target.closest('[data-artemax-favorites-close]')) { event.preventDefault(); self.close(); return; }
        // Click on the backdrop (outside the dialog box) closes, like the cart drawer.
        if (target === self.dialog && self.dialog.open) {
          var rect = self.dialog.getBoundingClientRect();
          var inside = event.clientX >= rect.left && event.clientX <= rect.right
            && event.clientY >= rect.top && event.clientY <= rect.bottom;
          if (!inside) self.close();
        }
      });
      this.dialog.addEventListener('close', function () { self.unlock(); });
    };

    ArtemaxFavoritesDrawer.prototype.open = function () {
      if (!this.dialog || this.dialog.open) return;
      this.scrollY = window.scrollY;
      document.documentElement.setAttribute('artemax-favorites-open', '');
      this.dialog.showModal();
      var list = this.dialog.querySelector('artemax-favorites-list');
      if (list && typeof list.fitSelects === 'function') list.fitSelects();
      var closeButton = this.dialog.querySelector('[data-artemax-favorites-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    };

    ArtemaxFavoritesDrawer.prototype.close = function () {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.close();
    };

    ArtemaxFavoritesDrawer.prototype.unlock = function () {
      document.documentElement.removeAttribute('artemax-favorites-open');
      var opener = this.querySelector('[data-artemax-favorites-open]');
      if (opener && this.isConnected) opener.focus({ preventScroll: true });
    };

    return ArtemaxFavoritesDrawer;
  })();

  window.ArtemaxFavorites = {
    version: 'favorites-v2.0',
    key: KEY,
    event: EVENT,
    list: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () { return read().length; },
    clear: function () { write([]); },
    sizedUrl: sizedUrl,
    addToCart: addToCart
  };

  document.addEventListener(EVENT, bindCounts);
  window.addEventListener('storage', function (event) {
    if (event.key === KEY || event.key === null) broadcast();
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) broadcast();
  });

  if (window.customElements) {
    if (!customElements.get('artemax-favorites-list')) customElements.define('artemax-favorites-list', ListElement);
    if (!customElements.get('artemax-favorites-drawer')) customElements.define('artemax-favorites-drawer', DrawerElement);
  }

  // Announce once at ready so surfaces that connected before this script
  // loaded (the product action row, the header) pick up their state.
  if (document.readyState !== 'loading') broadcast();
  else document.addEventListener('DOMContentLoaded', broadcast, { once: true });
})();
